import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { Pencil, Settings } from "lucide-react";
import GridReportService, {
  ReportSettingRow,
  RoleOption,
} from "../../../_services/GridReportService";
import { RolePicker } from "../../../access-control/RolePicker";

interface Props {
  show: boolean;
  onClose: () => void;
  /** Called after a successful save so the page can refresh its report dropdown. */
  onSaved?: () => void;
}

const ACCENT = "#7c3aed";

/**
 * Self-contained toggle switch (inline styles) — looks like a Bootstrap switch
 * but is immune to this app's global CSS overrides that break `Form.Check`
 * switch styling (it otherwise renders as a plain checkbox).
 */
const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 42,
      height: 24,
      borderRadius: 999,
      border: "none",
      padding: 0,
      cursor: disabled ? "default" : "pointer",
      background: checked ? ACCENT : "#cbd5e1",
      position: "relative",
      transition: "background 150ms",
      flexShrink: 0,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 2,
        left: checked ? 20 : 2,
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: "var(--surface-card)",
        transition: "left 150ms",
        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
      }}
    />
  </button>
);

const chip = (text: string): React.CSSProperties => ({
  background: "var(--surface-sunken)",
  color: "var(--text-body)",
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  padding: "2px 8px",
  fontSize: 12,
  fontWeight: 500,
  display: "inline-block",
  marginRight: 4,
  marginBottom: 4,
});

/**
 * Admin "Reports Settings" — configure each grid report's customer-facing
 * display name, enabled status (page guard) and allowed roles (role guard).
 * Mirrors the legacy Daily Reports admin screen.
 */
const ReportSettingsModal: React.FC<Props> = ({ show, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<ReportSettingRow[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);

  // Edit sub-modal state
  const [editing, setEditing] = useState<ReportSettingRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editEnabled, setEditEnabled] = useState(true);
  const [editRoleIds, setEditRoleIds] = useState<number[]>([]);
  const [editAxes, setEditAxes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const roleNameById = useMemo(() => {
    const m = new Map<number, string>();
    roles.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [roles]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GridReportService.getAdminSettings();
      setReports(data.reports);
      setRoles(data.roles);
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? "You need administrator privileges to manage report settings."
          : "Failed to load report settings."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) load();
  }, [show]);

  const openEdit = (r: ReportSettingRow) => {
    setEditing(r);
    setEditName(r.displayName);
    setEditEnabled(r.enabled);
    setEditRoleIds(r.roleIds || []);
    setEditAxes(r.axes || (r.availableAxes || []).map((a) => a.key));
  };

  const toggleAxis = (key: string) => {
    setEditAxes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      await GridReportService.updateReportSetting(editing.key, {
        displayName: editName,
        enabled: editEnabled,
        roleIds: editRoleIds,
        axes: editAxes,
      });
      // Reflect locally
      setReports((prev) =>
        prev.map((p) =>
          p.key === editing.key
            ? {
                ...p,
                displayName: editName || p.defaultDisplayName,
                enabled: editEnabled,
                roleIds: editRoleIds,
                axes: editAxes,
              }
            : p
        )
      );
      setEditing(null);
      onSaved?.();
    } catch (e: any) {
      setError("Failed to save. " + (e?.response?.data?.error || ""));
    } finally {
      setSaving(false);
    }
  };

  /** Quick inline enable/disable toggle (saves immediately). */
  const toggleEnabled = async (r: ReportSettingRow, enabled: boolean) => {
    setReports((prev) => prev.map((p) => (p.key === r.key ? { ...p, enabled } : p)));
    try {
      await GridReportService.updateReportSetting(r.key, {
        displayName: r.displayName,
        enabled,
        roleIds: r.roleIds,
      });
      onSaved?.();
    } catch {
      // revert on failure
      setReports((prev) => prev.map((p) => (p.key === r.key ? { ...p, enabled: !enabled } : p)));
      setError("Failed to update enabled status.");
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} size="xl" centered>
        <Modal.Header closeButton style={{ background: ACCENT, color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 8 }}>
            <Settings size={18} /> Reports Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {error && (
            <Alert variant="danger" style={{ margin: 12 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <Spinner animation="border" />
            </div>
          ) : (
            <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
              <table className="table table-hover" style={{ marginBottom: 0, fontSize: "0.88rem" }}>
                <thead style={{ position: "sticky", top: 0, background: "var(--surface-page)", zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 60 }}>Sl.No.</th>
                    <th>Display Name</th>
                    <th style={{ width: 110, textAlign: "center" }}>Enabled</th>
                    <th>Roles</th>
                    <th style={{ width: 80, textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r, i) => (
                    <tr key={r.key} style={{ opacity: r.enabled ? 1 : 0.55 }}>
                      <td>{i + 1}</td>
                      <td>
                        <div style={{ fontWeight: 600, color: "var(--text-strong)" }}>{r.displayName}</div>
                        <div style={{ fontSize: 11, color: "var(--text-faint)" }}>
                          {r.key}
                          {r.isComposite ? " · composite" : ""}
                          {r.availableAxes
                            ? ` · ${r.axes.length}/${r.availableAxes.length} axes`
                            : ""}
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "inline-flex", justifyContent: "center" }}>
                          <Toggle checked={r.enabled} onChange={(v) => toggleEnabled(r, v)} />
                        </div>
                      </td>
                      <td>
                        {(!r.roleIds || r.roleIds.length === 0) ? (
                          <span style={{ ...chip("All"), color: "var(--status-success-text)", background: "var(--status-success-bg)", borderColor: "var(--status-success-border)" }}>
                            All roles
                          </span>
                        ) : (
                          r.roleIds.map((id) => (
                            <span key={id} style={chip("")}>
                              {roleNameById.get(id) || `#${id}`}
                            </span>
                          ))
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => openEdit(r)}
                          title="Edit"
                          style={{ border: "1px solid var(--border-default)" }}
                        >
                          <Pencil size={15} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit sub-modal */}
      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton style={{ background: ACCENT, color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Edit Report: {editing?.defaultDisplayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Display Name</Form.Label>
            <Form.Control
              value={editName}
              placeholder={editing?.defaultDisplayName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <Form.Text muted>Shown to customers in the report dropdown.</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle checked={editEnabled} onChange={setEditEnabled} />
              <span style={{ fontSize: "0.9rem" }}>Enabled (visible to customers)</span>
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Roles</Form.Label>
            <RolePicker
              roles={roles}
              selected={editRoleIds}
              onChange={setEditRoleIds}
              accent={ACCENT}
              placeholder="All roles (click to restrict)"
            />
            <Form.Text muted>
              Leave empty to allow all roles. Otherwise only the selected roles (and Root) can see/run this report.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-1">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem", margin: 0 }}>
                Axes shown in X / Y dropdowns
              </Form.Label>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  className="gr-axis-bulk"
                  onClick={() => setEditAxes((editing?.availableAxes || []).map((a) => a.key))}
                >
                  All
                </button>
                <button
                  type="button"
                  className="gr-axis-bulk"
                  onClick={() => setEditAxes([])}
                >
                  None
                </button>
              </div>
            </div>
            <div className="gr-axis-grid">
              {(editing?.availableAxes || []).map((a) => {
                const checked = editAxes.includes(a.key);
                return (
                  <label key={a.key} className={`gr-axis-item${checked ? " is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAxis(a.key)}
                    />
                    <span>{a.label}</span>
                  </label>
                );
              })}
            </div>
            <Form.Text muted>
              Unchecked axes are hidden from this report's X and Y dropdowns. Leave all checked to show everything.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
            Close
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReportSettingsModal;
